import { useCallback, useEffect, useMemo, useState } from "react";
import useWeeklyPlanApi from "../api/use-weekly-plan-api";
import { WeeklyPlan, WeeklyPlanItem, WeeklyPlanItemUpdate } from "../types/weekly-plan";
import {
  addDays,
  formatWeekRange,
  formatWeekStart,
  getWeekStartDate,
} from "../utils/week-plan-date";
import { dispatchWeeklyPlanRefresh } from "../utils/weekly-plan-events";
import { trackEvent } from "../utils/firebase-analytics";

const WEEK_OPTIONS = [0, 7];

export const useWeeklyPlan = (brandId: string) => {
  const {
    getWeeklyPlan,
    generateWeeklyPlan,
    updateWeeklyPlanItem,
    deleteWeeklyPlanItem,
  } = useWeeklyPlanApi();
  const [weekStart, setWeekStart] = useState(formatWeekStart());
  const [plan, setPlan] = useState<WeeklyPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const weekOptions = useMemo(() => {
    const current = getWeekStartDate(new Date());
    return WEEK_OPTIONS.map((offset) => {
      const date = addDays(current, offset);
      return {
        value: formatWeekStart(date),
        label: `${offset === 0 ? "This week" : "Next week"} (${formatWeekRange(
          date
        )})`,
      };
    });
  }, []);

  const refreshPlan = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    const nextPlan = await getWeeklyPlan(brandId, weekStart);
    setPlan(nextPlan);
    setLoading(false);
  }, [brandId, getWeeklyPlan, weekStart]);

  useEffect(() => {
    void refreshPlan();
  }, [refreshPlan]);

  const generatePlan = useCallback(async () => {
    if (!brandId) return;
    setGenerating(true);
    const nextPlan = await generateWeeklyPlan(
      brandId,
      weekStart,
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    );
    setPlan(nextPlan);
    trackEvent("weekly_plan_generated", {
      item_count: nextPlan?.items.length ?? 0,
      week_start: weekStart,
    });
    dispatchWeeklyPlanRefresh(brandId);
    setGenerating(false);
  }, [brandId, generateWeeklyPlan, weekStart]);

  const updateItem = useCallback(
    async (itemId: string, updates: WeeklyPlanItemUpdate) => {
      const updated = await updateWeeklyPlanItem(itemId, updates);
      if (!updated) return false;
      setPlan((current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) =>
                item.id === itemId ? updated : item
              ),
            }
          : current
      );
      dispatchWeeklyPlanRefresh(updated.brand_id);
      return true;
    },
    [updateWeeklyPlanItem]
  );

  const deleteItem = useCallback(
    async (itemId: string) => {
      const deleted = await deleteWeeklyPlanItem(itemId);
      if (!deleted) return false;
      setPlan((current) =>
        current
          ? {
              ...current,
              items: current.items.filter((item) => item.id !== itemId),
            }
          : current
      );
      dispatchWeeklyPlanRefresh(brandId);
      return true;
    },
    [brandId, deleteWeeklyPlanItem]
  );

  const sortedItems = useMemo<WeeklyPlanItem[]>(() => {
    return [...(plan?.items ?? [])].sort(
      (left, right) =>
        new Date(left.planned_for).getTime() -
        new Date(right.planned_for).getTime()
    );
  }, [plan?.items]);

  return {
    weekStart,
    setWeekStart,
    weekOptions,
    plan,
    items: sortedItems,
    loading,
    generating,
    generatePlan,
    refreshPlan,
    updateItem,
    deleteItem,
  };
};

export default useWeeklyPlan;
